import {ClassGroup, CoverageGroupsLink} from 'Quoting/models/DTOs/quote';
import {A360ProductModel} from 'Quoting/models/Quoting';
import {getProductCdFromModel} from 'Quoting/utils/helpers';
import {createEisTypeObject, getByKeyPath, getCensusClassURI, isDefined, parsePath} from 'Quoting/utils/utilities';
import {List as IList, Map as IMap, fromJS} from 'immutable';

/*
  CASE 1: when the class being selected is not present in classGroups
  DO:
    add new object to classGroups and add corresponding ref to coverageGroupsLinks
    SUB CASE 1 (For Life Products Only): Add the new class to classGroups if it does not exist.
                                         Also, check if the other classes in classGroups are present in any of the other plans, if not remove them

  CASE 2: class is present in classGroups
    SUB CASE 1: class is not present in coverageGroupsLinks for selected plan
    DO: pick class ref from classGroups and create the ref object to coverageGroupsLinks
    (For Life Products Only): Also, check if the class being unselected is present anywhere else or not, if not : remove from classGroups

    SUB CASE 2: class is present in coverageGroupLinks for selected plan as well (when classes are selected and UI shows checkbox as checked)
    DO:
      a. remove the class ref from coverageGroupsLinks for selected plan and
      b. then check if any other plan has the same class in its coverageGroupsLinks for selected class and if not then remove from classGroups else do nothing
*/

const CENSUS_CLASS_GROUP_TYPE_SUFFIX = 'PredefinedClassificationGroup';
const CENSUS_COVERAGE_GROUP_LINK_SUFFIX = 'ClassificationGroupLink';

const createClassGroupObject = (censusClassURI: string, quoteModel: A360ProductModel, quoteRootId: string) => ({
  classGroupExtLink: {
    _uri: censusClassURI,
  },
  ...createEisTypeObject(
    `${getProductCdFromModel(quoteModel)}${CENSUS_CLASS_GROUP_TYPE_SUFFIX}`,
    quoteRootId,
    quoteRootId
  ),
});

export const createCoverageLinksObject = (
  quoteModel: A360ProductModel,
  selectedPlanRootId: string,
  selectedCoverageId: string,
  selectedCensusClassId: string
) => ({
  ...createEisTypeObject(
    `${getProductCdFromModel(quoteModel)}${CENSUS_COVERAGE_GROUP_LINK_SUFFIX}`,
    selectedPlanRootId,
    selectedCoverageId
  ),
  groupLink: {
    _ref: selectedCensusClassId,
  },
});

export const updateCensusClassOnPlanDeletion = (
  quoteData: IMap<string, any>,
  planIndex: number,
  coverageIndex: number = 0
) => {
  const coverageGroupLinks: IList<IMap<string, CoverageGroupsLink>> = getByKeyPath(
    quoteData,
    `benefitPackagingDetail.plans[${planIndex}].coverageDefs[${coverageIndex}].coverageGroupsLinks`
  ) as IList<any>;

  const coverageGroupLinksRest: IList<IMap<string, CoverageGroupsLink>> = (getByKeyPath(
    quoteData,
    'benefitPackagingDetail.plans'
  ) as IList<any>)
    .filter(
      p => getByKeyPath(quoteData, `benefitPackagingDetail.plans[${planIndex}]._key.id`) !== getByKeyPath(p, '_key.id')
    )
    .flatMap(p => p.get('coverageDefs'))
    .flatMap(covDef => (covDef as IMap<string, any>).get('coverageGroupsLinks'));

  let updatedClassGroups: IList<IMap<string, ClassGroup>> = quoteData.get('classGroups');

  coverageGroupLinks?.forEach(cgl => {
    const isClassLinkedToAnotherPlan = coverageGroupLinksRest.some(
      cl => getByKeyPath(cl, 'groupLink._ref') === getByKeyPath(cgl, 'groupLink._ref')
    );

    if (!isClassLinkedToAnotherPlan) {
      updatedClassGroups = updatedClassGroups.filter(
        cls => getByKeyPath(cls, '_key.id') !== getByKeyPath(cgl, 'groupLink._ref')
      );
    }
  });

  const updatedPlanList = (getByKeyPath(quoteData, 'benefitPackagingDetail.plans') as IList<any>)?.delete(planIndex);

  return {
    plans: updatedPlanList,
    classGroups: updatedClassGroups,
  };
};

export const handleCensusClassSelectionChange = (
  censusClassRootId: string,
  quote: IMap<string, any>,
  selectedPlanIndex: number
) => {
  if (!isDefined(censusClassRootId) || !isDefined(quote) || !isDefined(selectedPlanIndex)) return;

  const updatedQuoteData = quote.withMutations(quoteData => {
    const quoteRootId = getByKeyPath(quoteData, '_key.rootId') as string;

    const selectedPlanRootId = getByKeyPath(
      quoteData,
      `benefitPackagingDetail.plans[${selectedPlanIndex}]._key.rootId`
    ) as string;

    const quoteModel = quoteData.get('_modelName');
    const censusClassURI = getCensusClassURI(censusClassRootId);

    const selectedCoverageIndex = 0;

    const coverageDefsPath = `benefitPackagingDetail.plans[${selectedPlanIndex}].coverageDefs[${selectedCoverageIndex}]`;

    const coverageGroupLinksPath = `${coverageDefsPath}.coverageGroupsLinks`;

    const selectedCoverageId = getByKeyPath(quoteData, `${coverageDefsPath}._key.id`) as string;

    const existingClassInClassGroups = (getByKeyPath(quoteData, 'classGroups') as IList<any>)?.find(
      cg => getByKeyPath(cg, 'classGroupExtLink._uri') === getCensusClassURI(censusClassRootId)
    );

    // case 1: when no class is selected on any of the plans
    if (!existingClassInClassGroups) {
      const selectedCensusClass = createClassGroupObject(censusClassURI, quoteModel as A360ProductModel, quoteRootId);

      quoteData.set(
        'classGroups',
        (getByKeyPath(quoteData, 'classGroups') as IList<any>)?.push(fromJS(selectedCensusClass))
      );

      const updatedClassGroupLinks = createCoverageLinksObject(
        quoteModel as A360ProductModel,
        selectedPlanRootId,
        selectedCoverageId,
        selectedCensusClass._key.id
      );

      quoteData.setIn(
        parsePath(coverageGroupLinksPath),
        (getByKeyPath(quoteData, coverageGroupLinksPath) as IList<any>)?.push(fromJS(updatedClassGroupLinks))
      );
    } else {
      // case 2: class is present in classGroups
      const existingRefInCoverageGroupLinks = (getByKeyPath(quoteData, coverageGroupLinksPath) as IList<any>)?.find(
        cgl => getByKeyPath(cgl, 'groupLink._ref') === getByKeyPath(existingClassInClassGroups, '_key.id')
      );

      // subcase 1
      if (!existingRefInCoverageGroupLinks) {
        const updatedClassGroupLinks = createCoverageLinksObject(
          quoteModel as A360ProductModel,
          selectedPlanRootId,
          selectedCoverageId,
          getByKeyPath(existingClassInClassGroups, '_key.id') as string
        );

        quoteData.setIn(
          parsePath(coverageGroupLinksPath),
          (getByKeyPath(quoteData, coverageGroupLinksPath) as IList<any>)?.push(fromJS(updatedClassGroupLinks))
        );
      } else {
        // subcase 2
        const remainingCoverageGroupLinks = (getByKeyPath(quoteData, coverageGroupLinksPath) as IList<any>)?.filter(
          cgl => getByKeyPath(cgl, 'groupLink._ref') !== getByKeyPath(existingClassInClassGroups, '_key.id')
        );

        quoteData.setIn(parsePath(coverageGroupLinksPath), remainingCoverageGroupLinks);

        const isClassUsedInOtherPlans = (getByKeyPath(quoteData, 'benefitPackagingDetail.plans') as IList<
          any
        >)?.some(plan =>
          plan
            .get('coverageDefs')
            ?.some(coverage =>
              coverage
                .get('coverageGroupsLinks')
                ?.some(
                  cgl => getByKeyPath(cgl, 'groupLink._ref') === getByKeyPath(existingClassInClassGroups, '_key.id')
                )
            )
        );

        if (!isClassUsedInOtherPlans) {
          const remainingClassGroups = quoteData
            .get('classGroups')
            ?.filter(
              cg =>
                getByKeyPath(cg, 'classGroupExtLink._uri') !==
                getByKeyPath(existingClassInClassGroups, 'classGroupExtLink._uri')
            );

          quoteData.set('classGroups', remainingClassGroups);
        }
      }
    }
  });

  return updatedQuoteData;
};

export const handleCensusClassSelectionChangeLife = (
  censusClassRootId: string,
  quote: IMap<string, any>,
  selectedPlanIndex: number
) => {
  if (!isDefined(censusClassRootId) || !isDefined(quote) || !isDefined(selectedPlanIndex)) return;

  const updatedQuoteData = quote.withMutations(quoteData => {
    const quoteRootId = getByKeyPath(quoteData, '_key.rootId') as string;

    const selectedPlanRootId = getByKeyPath(
      quoteData,
      `benefitPackagingDetail.plans[${selectedPlanIndex}]._key.rootId`
    ) as string;

    const quoteModel = quoteData.get('_modelName');
    const censusClassURI = getCensusClassURI(censusClassRootId);

    const coverageDefsPathForCurrentPlan = `benefitPackagingDetail.plans[${selectedPlanIndex}].coverageDefs`;

    const existingClassInClassGroups = (getByKeyPath(quoteData, 'classGroups') as IList<any>)?.find(
      cg => getByKeyPath(cg, 'classGroupExtLink._uri') === getCensusClassURI(censusClassRootId)
    );

    // case 1: when no class is selected on any of the plans
    if (!existingClassInClassGroups) {
      const selectedCensusClass = createClassGroupObject(censusClassURI, quoteModel as A360ProductModel, quoteRootId);

      quoteData.set(
        'classGroups',
        (getByKeyPath(quoteData, 'classGroups') as IList<any>)?.push(fromJS(selectedCensusClass))
      );

      quoteData.updateIn(parsePath(coverageDefsPathForCurrentPlan), (coverageDefs: IList<any>) => {
        return coverageDefs.map((covDef, covIndex) =>
          covDef.set(
            'coverageGroupsLinks',
            IList.of(
              fromJS(
                createCoverageLinksObject(
                  quoteModel as A360ProductModel,
                  selectedPlanRootId,
                  getByKeyPath(
                    quoteData,
                    `benefitPackagingDetail.plans[${selectedPlanIndex}].coverageDefs[${covIndex}]._key.id`
                  ) as string,
                  selectedCensusClass._key.id
                )
              )
            )
          )
        );
      });

      const coverageGroupLinksRefs = (getByKeyPath(quoteData, 'benefitPackagingDetail.plans') as IList<any>)
        ?.flatMap(p => p.get('coverageDefs')?.flatMap(covDef => covDef.get('coverageGroupsLinks')))
        ?.map((cgl: any) => getByKeyPath(cgl, 'groupLink._ref'))
        ?.toSet();

      quoteData.set(
        'classGroups',
        (getByKeyPath(quoteData, 'classGroups') as IList<any>)?.filter(cg =>
          coverageGroupLinksRefs.has(getByKeyPath(cg, '_key.id'))
        )
      );
    } else {
      // case 2: class is present in classGroups (may be from another plan within the same quote) but not selected for current plan
      const existingRefInCoverageGroupLinks = (getByKeyPath(quoteData, coverageDefsPathForCurrentPlan) as IList<any>)
        ?.flatMap(covDef => covDef.get('coverageGroupsLinks'))
        ?.some(matchClassRef(existingClassInClassGroups));

      // subcase 1: for each coverage of current plan, add the reference object in the coverageGroupLinks
      // For Life: Check if the class being unselected is present anywhere else, if not remove it from classGroups
      if (!existingRefInCoverageGroupLinks) {
        quoteData.updateIn(parsePath(coverageDefsPathForCurrentPlan), coverageDefs => {
          return (coverageDefs as IList<any>).map((covDef, covIndex) =>
            covDef.set(
              'coverageGroupsLinks',
              IList.of(
                fromJS(
                  createCoverageLinksObject(
                    quoteModel as A360ProductModel,
                    selectedPlanRootId,
                    getByKeyPath(
                      quoteData,
                      `benefitPackagingDetail.plans[${selectedPlanIndex}].coverageDefs[${covIndex}]._key.id`
                    ) as string,
                    getByKeyPath(existingClassInClassGroups, '_key.id') as string
                  )
                )
              )
            )
          );
        });
        const coverageGroupLinksRefs = (getByKeyPath(quoteData, 'benefitPackagingDetail.plans') as IList<any>)
          ?.flatMap(p => p.get('coverageDefs')?.flatMap(covDef => covDef.get('coverageGroupsLinks')))
          ?.map((cgl: any) => getByKeyPath(cgl, 'groupLink._ref'))
          ?.toSet();

        quoteData.set(
          'classGroups',
          (getByKeyPath(quoteData, 'classGroups') as IList<any>)?.filter(cg =>
            coverageGroupLinksRefs.has(getByKeyPath(cg, '_key.id'))
          )
        );
      } else {
        // subcase 2: Class is already selected in current plan, here we need to remove (mark unchecked)
        quoteData.updateIn(parsePath(coverageDefsPathForCurrentPlan), coverageDefs => {
          return (coverageDefs as IList<any>).map(covDef => covDef.set('coverageGroupsLinks', IList()));
        });

        const isClassUsedInOtherPlans = (getByKeyPath(quoteData, 'benefitPackagingDetail.plans') as IList<
          any
        >)?.some(plan =>
          plan
            .get('coverageDefs')
            ?.some(coverage => coverage.get('coverageGroupsLinks')?.some(matchClassRef(existingClassInClassGroups)))
        );

        if (!isClassUsedInOtherPlans) {
          const remainingClassGroups = quoteData
            .get('classGroups')
            ?.filter(
              cg =>
                getByKeyPath(cg, 'classGroupExtLink._uri') !==
                getByKeyPath(existingClassInClassGroups, 'classGroupExtLink._uri')
            );

          quoteData.set('classGroups', remainingClassGroups);
        }
      }
    }
  });

  return updatedQuoteData;
};
function matchClassRef(
  existingClassInClassGroups: IMap<string, ClassGroup>
): (coverageGroupsLink: IMap<string, CoverageGroupsLink>) => boolean {
  return (cgl: any) => getByKeyPath(cgl, 'groupLink._ref') === getByKeyPath(existingClassInClassGroups, '_key.id');
}
